import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Loader2, Upload } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

interface ImageAnalyzerProps {
  onInspirationSelect: (text: string) => void;
  onClose: () => void;
}

export const ImageAnalyzer = ({ onInspirationSelect, onClose }: ImageAnalyzerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);

  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
    handleAnalyzeImage(file);
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleImageSelect(file);
    } else {
      toast.error("Please drop an image file");
    }
  }, []);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleAnalyzeImage = async (imageFile: File) => {
    setIsLoading(true);
    const loadingToastId = toast.loading("Analyzing image...");

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Upload image to Supabase Storage
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('inspiration_images')
        .upload(fileName, imageFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('inspiration_images')
        .getPublicUrl(fileName);

      // Analyze image
      const { data, error } = await supabase.functions.invoke('analyze-image', {
        body: { imageUrl: publicUrl },
      });

      if (error) throw error;
      
      toast.dismiss(loadingToastId);
      setAnalysis(data.topicSummary);
    } catch (error) {
      console.error("Error analyzing image:", error);
      toast.dismiss(loadingToastId);
      toast.error("Failed to analyze image");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateContent = async () => {
    if (!analysis) return;

    setIsGeneratingContent(true);
    onClose(); // Close modal smoothly
    const loadingToastId = toast.loading("Generating content...");

    try {
      const { data, error } = await supabase.functions.invoke('generate-from-topic', {
        body: { topic: analysis },
      });

      if (error) throw error;

      toast.dismiss(loadingToastId);
      onInspirationSelect(data.generatedText);
      toast.success("Content generated successfully!");
    } catch (error) {
      console.error("Error generating content:", error);
      toast.dismiss(loadingToastId);
      toast.error("Failed to generate content");
    } finally {
      setIsGeneratingContent(false);
    }
  };

  return (
    <div className="h-full flex flex-col space-y-4 overflow-y-auto">
      <div 
        className="flex flex-col items-center gap-4"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <div className={`
          w-full border-2 border-dashed rounded-lg p-8 
          ${previewUrl ? 'border-primary' : 'border-gray-300'} 
          transition-colors duration-200 
          flex flex-col items-center justify-center gap-4
          cursor-pointer
          hover:border-primary/80
        `}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="hidden"
            id="image-upload"
          />
          {!previewUrl && (
            <label
              htmlFor="image-upload"
              className="flex flex-col items-center gap-2 cursor-pointer"
            >
              <Upload className="h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-600">
                Drop an image here or click to select
              </p>
            </label>
          )}
          
          {previewUrl && (
            <div className="relative w-full aspect-video">
              <img
                src={previewUrl}
                alt="Selected"
                className="rounded-lg object-cover w-full h-full"
              />
            </div>
          )}
        </div>
        
        {analysis && (
          <Card className="w-full p-4">
            <h3 className="text-sm font-medium mb-2">Topic Summary</h3>
            <Textarea
              value={analysis}
              onChange={(e) => setAnalysis(e.target.value)}
              className="min-h-[100px] text-sm text-gray-600"
              placeholder="Edit the topic summary..."
            />
          </Card>
        )}
        
        <Button 
          className="w-full" 
          onClick={handleGenerateContent} 
          disabled={isGeneratingContent || !analysis}
        >
          {isGeneratingContent ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            'Generate Content'
          )}
        </Button>
      </div>
    </div>
  );
};